import apiService from "./api";
import type {
	Function,
	CreateFunctionDto,
	UpdateFunctionDto,
	FindFunctionDto,
	FunctionStatistics,
} from "../types/function";

export interface FunctionFilter {
	search?: string;
	page?: number;
	limit?: number;
	orderBy?: string;
	order?: "asc" | "desc";
	name?: string;
	category?: string;
	isActive?: boolean;
}

export interface PaginatedResponse<T> {
	data: T[];
	meta: {
		total: number;
		page: number;
		limit: number;
		pages: number;
	};
}

// Function CRUD operations
export const getFunctions = async (
	filter: FindFunctionDto = {}
): Promise<PaginatedResponse<Function>> => {
	try {
		const response = await apiService.get("/functions", { params: filter });

		// Se o response for um array (versão antiga), wrap em PaginatedResponse
		if (Array.isArray(response)) {
			return {
				data: response,
				meta: {
					total: response.length,
					page: 1,
					limit: response.length,
					pages: 1,
				},
			};
		}

		// Se já vier no formato paginado, retorna diretamente
		return (
			response || {
				data: [],
				meta: { total: 0, page: 1, limit: 10, pages: 0 },
			}
		);
	} catch (error) {
		console.error("Erro na requisição getFunctions:", error);
		return {
			data: [],
			meta: { total: 0, page: 1, limit: 10, pages: 0 },
		};
	}
};

export const getFunctionById = async (id: string): Promise<Function> => {
	const response = await apiService.get(`/functions/${id}`);
	return response;
};

export const getFunctionByName = async (name: string): Promise<Function> => {
	const response = await apiService.get(`/functions/name/${name}`);
	return response;
};

export const createFunction = async (
	functionData: CreateFunctionDto
): Promise<Function> => {
	const response = await apiService.post("/functions", functionData);
	return response;
};

export const updateFunction = async (
	id: string,
	functionData: UpdateFunctionDto
): Promise<Function> => {
	const response = await apiService.patch(`/functions/${id}`, functionData);
	return response;
};

export const deleteFunction = async (id: string): Promise<void> => {
	await apiService.delete(`/functions/${id}`);
};

export const hardDeleteFunction = async (id: string): Promise<void> => {
	await apiService.delete(`/functions/${id}/hard`);
};

export const getFunctionCategories = async (): Promise<string[]> => {
	try {
		const response = await apiService.get("/functions/categories");
		return response || [];
	} catch (error) {
		console.error("Erro na requisição getFunctionCategories:", error);
		return [];
	}
};

export const getFunctionStatistics = async (): Promise<FunctionStatistics> => {
	try {
		const response = await apiService.get("/functions/statistics");
		return (
			response || {
				total: 0,
				withPersons: 0,
				withoutPersons: 0,
				byCategory: [],
			}
		);
	} catch (error) {
		console.error("Erro na requisição getFunctionStatistics:", error);
		return {
			total: 0,
			withPersons: 0,
			withoutPersons: 0,
			byCategory: [],
		};
	}
};

export default {
	getFunctions,
	getFunctionById,
	getFunctionByName,
	createFunction,
	updateFunction,
	deleteFunction,
	hardDeleteFunction,
	getFunctionCategories,
	getFunctionStatistics,
};
